import React, { createContext, useContext, useEffect, useState } from 'react';
import type { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { enrollNewUserInOnboarding, unenrollFromAccountDeletionFlow, unenrollFromReactivationFlow } from '@/api/mailerlite';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Listen for auth changes - single source of truth
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        // Only process meaningful auth events, ignore token refreshes
        // TOKEN_REFRESHED happens every ~55 minutes and doesn't require any action
        if (event === 'TOKEN_REFRESHED') {
          // Just update session silently, don't trigger any other logic
          setSession(session);
          return;
        }

        // Enroll new users in onboarding flow (only on initial sign in)
        if (event === 'SIGNED_IN' && session?.user) {
          // Check if this is a new user (created within last 5 seconds)
          const userCreatedAt = new Date(session.user.created_at);
          const now = new Date();
          const isNewUser = (now.getTime() - userCreatedAt.getTime()) < 5000;

          if (isNewUser) {
            // Enroll in onboarding email flow (non-blocking)
            const displayName = session.user.user_metadata?.full_name ||
              session.user.user_metadata?.name ||
              session.user.email?.split('@')[0] || '';

            enrollNewUserInOnboarding(
              session.user.id,
              session.user.email || '',
              displayName
            );
          } else {
            // If user reactivated, remove from reactivation + account deletion flows (non-blocking)
            unenrollFromReactivationFlow();
            unenrollFromAccountDeletionFlow();
          }

          // Track successful auth events
          if (typeof window !== 'undefined' && window.plausible) {
            window.plausible('User Signed In', {
              props: {
                method: session.user.app_metadata?.provider || 'email',
                is_new_user: isNewUser ? 'true' : 'false'
              }
            });
          }
        }

        // Update auth state for all other events
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Track sign out
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible('User Signed Out');
    }

    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/app`
      }
    });
    return { error };
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signOut,
    signInWithGoogle,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
